/**
 * window.__ld (I1) — the QA handle from contracts/debug.ts. Installed in dev and whenever the URL has ?qa.
 *
 *   await window.__ld.ready;                                   // phase reached explore (or the intro was skipped)
 *   window.__ld.store.getState().actions.select("pune-international-airport");
 *   await window.__ld.perf.sample(3000);                       // rAF frame times: p50 / p95 / max
 *   window.__ld.controller()?.map() · window.__ld.choreo()?.debug() · window.__ld.skipIntro()
 *
 * Extras (not in the contract): kiosk (B6 kioskDebug), preload() (B1 status), bus (the typed event bus).
 */
import type { Choreographer, MapController } from "@/contracts/map";
import type { LdDebug, PerfSample } from "@/contracts/debug";
import type { Phase } from "@/contracts/store";
import { skipIntro } from "@/features/intro/IntroOverlay";
import { kioskDebug } from "@/features/kiosk/useKiosk";
import { preload } from "@/features/loader/preload";
import { bus, on } from "@/store/bus";
import { appStore } from "@/store/store";

export interface DebugSources {
  controller: () => MapController | null;
  choreo: () => Choreographer | null;
}

export type LdDebugExt = LdDebug & {
  kiosk: typeof kioskDebug;
  preload: () => ReturnType<typeof preload.status>;
  bus: typeof bus;
};

let sources: DebugSources = { controller: () => null, choreo: () => null };

/** Phases in which the app is up and interactive (the contract's "ready"). */
const READY_PHASES: ReadonlySet<Phase> = new Set<Phase>(["explore", "drive", "attract"]);

/** Frame-interval statistics (ms). Pure; exported for tests. */
export function frameStats(intervals: readonly number[]): PerfSample {
  const n = intervals.length;
  if (!n) return { frames: 0, p50: 0, p95: 0, max: 0, droppedOver33: 0 };
  const s = [...intervals].sort((a, b) => a - b);
  const q = (p: number) => s[Math.min(n - 1, Math.max(0, Math.ceil(p * n) - 1))];
  const r = (v: number) => Math.round(v * 100) / 100;
  return { frames: n, p50: r(q(0.5)), p95: r(q(0.95)), max: r(s[n - 1]), droppedOver33: s.filter((v) => v > 33.4).length };
}

/** Samples requestAnimationFrame intervals for `ms`. */
export function sampleFrames(ms: number): Promise<PerfSample> {
  return new Promise((resolve) => {
    const out: number[] = [];
    let last = 0;
    let end = 0;
    const step = (now: number) => {
      if (last) out.push(now - last);
      else end = now + Math.max(0, ms);
      last = now;
      if (now < end || !end) requestAnimationFrame(step);
      else resolve(frameStats(out));
    };
    requestAnimationFrame(step);
  });
}

/** Resolves when the phase first reaches explore / drive / attract. */
function readyPromise(): Promise<void> {
  return new Promise<void>((resolve) => {
    if (READY_PHASES.has(appStore.getState().phase)) return resolve();
    let offBus: () => void = () => {};
    const unsub = appStore.subscribe((s) => {
      if (!READY_PHASES.has(s.phase)) return;
      unsub();
      offBus();
      resolve();
    });
    offBus = on("intro:done", () => {
      unsub();
      offBus();
      resolve();
    });
  });
}

export function debugWanted(search: string = typeof location !== "undefined" ? location.search : ""): boolean {
  return import.meta.env.DEV || new URLSearchParams(search).has("qa");
}

/** Installs window.__ld once (idempotent; later calls update the sources). */
export function installDebug(src: DebugSources): LdDebugExt | null {
  if (typeof window === "undefined") return null;
  const existing = window.__ld as LdDebugExt | undefined;
  sources = src;
  if (existing) return existing;
  const ld: LdDebugExt = {
    ready: readyPromise(),
    store: appStore,
    controller: () => sources.controller(),
    choreo: () => sources.choreo(),
    perf: { sample: sampleFrames },
    skipIntro: () => {
      skipIntro();
    },
    get version(): string {
      return preload.status().version ?? "";
    },
    kiosk: kioskDebug,
    preload: () => preload.status(),
    bus,
  };
  window.__ld = ld;
  return ld;
}
